import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import { DataRecordOrdering } from 'ui/types/data-ordering';
import Translate from 'ui/atoms/translate';
import {
  AdminApi,
  AdminBundesbankSettlementInstruction,
  AdminBundesbankSettlementInstructionsListOrderingEnum,
  AdminBundesbankSettlementInstructionsValuesRetrieveFieldEnum,
} from 'api';
import StatusTag from 'ui/atoms/status-tag';
import { PaginationProps } from 'ui/molecules/pagination';
import Currency from 'src/ui/atoms/currency';
import { useTriggerSolutionStatus } from '../hooks';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { Search } from 'src/ui/molecules/filtering';

export interface SettlementsTableProps {
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  settlements?: AdminBundesbankSettlementInstruction[];
  goToDetails: (id: string) => void;
  title?: ReactNode;
  hasPermission: boolean;
  loading?: boolean;
  paginationProps?: PaginationProps;
  defaultLimit?: number;
}

const StudioSettlementsTable: FunctionComponent<SettlementsTableProps> = (props) => {
  const { ordering, onOrderBy, settlements, goToDetails, title, hasPermission, loading, paginationProps } = props;

  const { getStatusName, getVariant } = useTriggerSolutionStatus();

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const settlementStatusFilterOptions = useMemo(() => {
    return [
      {
        id: 'asset_locked',
        name: translate('studioSettlementsTable.assetLocked'),
      },
      {
        id: 'created',
        name: translate('studioSettlementsTable.created'),
      },
      {
        id: 'failed',
        name: translate('studioSettlementsTable.failed'),
      },
      {
        id: 'payment_instruction_prepared',
        name: translate('studioSettlementsTable.paymentInstructionPrepared'),
      },
      {
        id: 'success',
        name: translate('studioSettlementsTable.success'),
      },
    ];
  }, [translate]);

  return (
    <DataRecordTable
      ordering={ordering}
      onOrderBy={onOrderBy}
      data={settlements}
      title={title}
      loading={loading}
      hasPermission={hasPermission}
      paginationProps={paginationProps}
      onGoToDetails={goToDetails}
      searchComponent={
        <Search input="search" defaultField="search" label={translate('filters.labels.correlationId')} name="search" />
      }
      cells={[
        {
          title: <Translate name={`studioSettlementsTable.correlationID`} />,
          render: ({ correlationId }) => correlationId,
          orderingFieldName: AdminBundesbankSettlementInstructionsListOrderingEnum.CORRELATION_ID_ASC,
          type: CellTypeEnum.text,
          filterName: 'correlationId',
          searchFieldName: AdminBundesbankSettlementInstructionsValuesRetrieveFieldEnum.CORRELATION_ID,
          getFilterData: (params: any) => adminApi.adminBundesbankSettlementInstructionsValuesRetrieve(params),
        },
        {
          title: <Translate name={`studioSettlementsTable.assetAmount`} />,
          render: ({ productAmount }) => productAmount,
          orderingFieldName: AdminBundesbankSettlementInstructionsListOrderingEnum.PRODUCT_AMOUNT_ASC,
          type: CellTypeEnum.number,
          filterName: 'productAmount',
        },
        {
          title: <Translate name={`studioSettlementsTable.paymentAmount`} />,
          alignContent: 'right',
          render: ({ paymentAmount }) => <Currency>{paymentAmount}</Currency>,
          orderingFieldName: AdminBundesbankSettlementInstructionsListOrderingEnum.PAYMENT_AMOUNT_ASC,
          type: CellTypeEnum.number,
          filterName: 'paymentAmount',
        },
        {
          title: <Translate name={`studioSettlementsTable.status`} />,
          render: ({ status }) => (
            <StatusTag variant={getVariant(status)} truncate={true}>
              {getStatusName(status)}
            </StatusTag>
          ),
          orderingFieldName: AdminBundesbankSettlementInstructionsListOrderingEnum.STATUS_ASC,
          type: CellTypeEnum.text,
          filterName: 'status',
          getFilterData: () => {
            return settlementStatusFilterOptions;
          },
        },
      ]}
    />
  );
};

export default StudioSettlementsTable;
